import { css } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import ReactMapGL, {
  FullscreenControl,
  GeolocateControl,
  Layer,
  MapRef,
  Marker,
  NavigationControl,
  Source
} from 'react-map-gl'
import Center from 'assets/icons/pin-test.svg'
import Selected from 'assets/icons/selected-3.svg'
import { unifiedColors } from 'styles/colors'
import {
  BackToStartButton,
  MapDisplayButton,
  MapDisplayButtons
} from './MapContainer.style'
import { NEXT_PUBLIC_MAPBOX_TOKEN } from './map-env'
import 'mapbox-gl/dist/mapbox-gl.css'

interface Props {
  longitude: number
  latitude: number
  geoJson?: GeoJSON.FeatureCollection
  onLoad: () => void
  bounds?: [number, number, number, number] | null
}

export default function Map({
  latitude,
  longitude,
  geoJson,
  onLoad,
  bounds
}: Props) {
  const { t } = useTranslation('event')
  const mapRef = useRef<MapRef>(null)
  const [isSatellite, setSatellite] = useState(false)
  const [isFullscreenEnabled, setFullscreen] = useState(false)
  const mapStyle = isSatellite
    ? 'mapbox://styles/mapbox/satellite-v9'
    : 'mapbox://styles/peyce/cl30azfve005k14qnod0isbro'

  useEffect(() => {
    if (
      window?.document?.fullscreenEnabled ||
      ('mozFullscreenEnabled' in window?.document &&
        window.document.mozFullscreenEnabled) ||
      ('msFullscreenEnabled' in window?.document &&
        window.document.msFullscreenEnabled) ||
      ('webkitFullscreenEnabled' in window?.document &&
        window.document.webkitFullscreenEnabled)
    ) {
      setFullscreen(true)
    }
  }, [])

  return (
    <ReactMapGL
      ref={mapRef}
      initialViewState={
        bounds
          ? {
              bounds,
              fitBoundsOptions: {
                padding: 50
              }
            }
          : {
              longitude,
              latitude,
              zoom: 12
            }
      }
      mapboxAccessToken={NEXT_PUBLIC_MAPBOX_TOKEN}
      mapStyle={mapStyle}
      style={{
        width: '100%',
        height: '100vh',
        maxHeight: '500px',
        borderRadius: '20px'
      }}
      attributionControl={false}
      cooperativeGestures={true}
      onLoad={onLoad}
    >
      <MapDisplayButtons>
        <MapDisplayButton
          onClick={() => setSatellite(false)}
          css={{ borderRadius: '4px 0 0 4px' }}
          $isActive={!isSatellite}
        >
          {t('default')}
        </MapDisplayButton>
        <MapDisplayButton
          $isActive={isSatellite}
          css={css`
            border-radius: 0 4px 4px 0;
            border-left: 1px solid ${unifiedColors.surface.tertiary};
          `}
          onClick={() => setSatellite(true)}
        >
          {t('satellite')}
        </MapDisplayButton>
      </MapDisplayButtons>
      <div
        css={css`
          position: absolute;
          right: 10px;
          top: ${isFullscreenEnabled ? 185 : 148}px;
        `}
      >
        <BackToStartButton
          onClick={() => {
            mapRef.current?.flyTo({
              center: [longitude, latitude],
              duration: 200,
              zoom: 12
            })
          }}
        >
          <Center
            css={css`
              height: 20px;
              width: 20px;
            `}
          />
        </BackToStartButton>
      </div>
      {geoJson && (
        <Source type="geojson" data={geoJson} id="gpx">
          <Layer
            {...{
              id: 'data',
              type: 'line',
              source: 'gpx',
              paint: {
                'line-color': unifiedColors.button.primary.default,
                'line-width': 5
              }
            }}
          />
        </Source>
      )}
      <Marker latitude={latitude} longitude={longitude}>
        <Selected
          css={css`
            margin-bottom: 46px;
            width: 42px !important;
          `}
        />
      </Marker>
      <GeolocateControl position="top-right" />
      <FullscreenControl position="top-right" />
      <NavigationControl position="top-right" />
    </ReactMapGL>
  )
}
